/** THE DEBRIEF — daily 5pm Fieldy digest tile + full view.
 * Privacy: demo/presenting mode masks COMPLETELY — no transcript/summary text
 * in the DOM while presenting; full view inaccessible. Internal only. */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TXT, SUB, TEAL, AMBER, MAGENTA, VIOLET, BORDER, HEAD, mini } from './V2DeskTiles';
import { PeopleTab, pidOf } from './V2DeskPeople';

const CARD = { border: `1px solid ${BORDER}`, borderRadius: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.02)' };
const secH = { margin: '18px 0 8px', fontFamily: HEAD, fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: SUB };
const bodyP = { margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, lineHeight: 1.6, color: TXT };

// ── Error boundary — a bad digest shape (or a null downstream) can never
// hard-blank the Debrief again. Renders a clean "couldn't load" card with
// a Retry that resets the boundary so day-nav keeps working.
class DebriefErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    try { console.error('[Debrief] crash caught:', error, info); } catch { /* noop */ }
  }
  reset = () => this.setState({ error: null });
  render() {
    if (!this.state.error) return this.props.children;
    const msg = (this.state.error && this.state.error.message) || 'Unknown render error';
    const onClose = this.props.onClose;
    return createPortal(
      <div data-testid="debrief-error-boundary" onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(8,9,11,0.82)', backdropFilter: 'blur(10px)', display: 'grid', placeItems: 'center', padding: 20 }}>
        <div onClick={(e) => e.stopPropagation()}
          style={{ width: 'min(460px, 92vw)', background: '#0B0C0E', border: `1px solid ${BORDER}`, borderRadius: 18, padding: 26, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 26 }}>🎧</p>
          <p style={{ margin: '10px 0 4px', fontFamily: HEAD, fontSize: 15, fontWeight: 800, color: TXT }}>Couldn&apos;t load this day</p>
          <p style={{ margin: '0 0 14px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, lineHeight: 1.5 }}>The Debrief hit a snag on this render. Nothing lost — try again, or close and pick a different day.</p>
          <p style={{ margin: '0 0 16px', fontFamily: 'Menlo, monospace', fontSize: 10, color: 'rgba(255,180,105,0.6)', wordBreak: 'break-word' }}>{String(msg).slice(0, 200)}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button data-testid="debrief-error-retry" onClick={this.reset} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>↻ Retry</button>
            <button data-testid="debrief-error-close" onClick={onClose} style={{ ...mini, color: MAGENTA, borderColor: 'rgba(236,124,36,0.4)' }}>✕ Close</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
}

const fmtDate = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
const shiftDate = (s, n) => { const d = new Date(s + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

// ── TILE ─────────────────────────────────────────────────────────────────────
export const DebriefTile = ({ ctx, onOpen }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => { ctx.req('GET', '/api/admin/desk/debrief').then(setD).catch(() => {}); }, [ctx.req]);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  if (ctx.demo) {
    return (
      <div data-testid="debrief-tile-masked" style={{ textAlign: 'center', padding: '14px 6px' }}>
        <p style={{ margin: 0, fontSize: 20 }}>🎧</p>
        <p style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, fontWeight: 700 }}>Debrief · hidden while presenting</p>
      </div>
    );
  }
  if (!d) return <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB }}>…</p>;
  if (!d.connected) {
    return (
      <div data-testid="debrief-tile-needskey" style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 12, border: `1px dashed ${BORDER}` }}>
        <p style={{ margin: 0, fontSize: 18 }}>🎧</p>
        <p style={{ margin: '5px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, lineHeight: 1.5 }}>Fieldy not connected — needs webhook token</p>
      </div>
    );
  }
  const dg = d.digest;
  const glow = d.owed?.glow ? { boxShadow: `0 0 22px ${AMBER}33`, border: `1px solid ${AMBER}55`, borderRadius: 12 } : {};
  return (
    <div data-testid="debrief-tile" onClick={() => onOpen()} style={{ cursor: 'pointer', padding: 8, margin: -8, ...glow }}>
      {d.after5 && dg ? (
        <>
          <p style={{ margin: 0, fontFamily: HEAD, fontSize: 13, fontWeight: 800, color: TEAL }}>
            {dg.conv_count || 0} conversations · {(dg.promises || []).length} promises · {(dg.actions || []).length} actions
          </p>
          <p style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: TXT, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {dg.narrative_summary}
          </p>
        </>
      ) : (
        <>
          <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: TXT, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {d.yesterday_headline || 'No debrief yet.'}
          </p>
          <p style={{ margin: '7px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB, fontWeight: 700 }}>🕔 today's debrief lands at 5pm</p>
        </>
      )}
      {(d.owed?.top || []).length > 0 && (
        <OwedStrip owed={d.owed} ctx={ctx} reload={load}
          onJump={(x) => onOpen({ person: x.person_id, promise: x.promise_id })} />
      )}
    </div>
  );
};

// ── ⚡ PROMISES YOU OWE strip ────────────────────────────────────────────────
const OwedStrip = ({ owed, ctx, onJump, reload }) => {
  const mark = async (e, x, action) => {
    e.stopPropagation();
    try {
      await ctx.req('POST', '/api/admin/desk/debrief/promise_mark', { date: x.date, promise_id: x.promise_id, action });
      if (action === 'done') { ctx.showToast('🏆 Promise kept — on the Win wall'); ctx.load(); }
      else ctx.showToast('✕ Dismissed — no longer relevant', async () => {
        await ctx.req('POST', '/api/admin/desk/debrief/promise_mark', { date: x.date, promise_id: x.promise_id, action: 'undo_dismiss' });
        reload();
      });
      reload();
    } catch { ctx.showToast('Could not update — try again'); }
  };
  const add = async (e, x) => {
    e.stopPropagation();
    try { await ctx.req('POST', '/api/admin/desk/debrief/promise_add', { date: x.date, promise_id: x.promise_id }); ctx.showToast('✅ Added to Desk'); ctx.load(); reload(); }
    catch { ctx.showToast('Could not add — try again'); }
  };
  return (
    <div data-testid="debrief-owed-strip" style={{ marginTop: 9, borderTop: `1px solid ${BORDER}`, paddingTop: 7 }}>
      <p style={{ margin: '0 0 4px', fontFamily: HEAD, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em', color: AMBER }}>
        ⚡ YOU OWE{owed.total > (owed.top || []).length ? ` · TOP ${owed.top.length} OF ${owed.total}` : ''}
      </p>
      {(owed.top || []).map((x) => (
        <div key={x.promise_id} data-testid={`owed-row-${x.promise_id}`} onClick={(e) => { e.stopPropagation(); onJump(x); }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 0', cursor: 'pointer' }}>
          <p style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {x.what} · <b style={{ color: TEAL }}>{x.person_name}</b> · <span style={{ color: x.overdue || x.age_days > 3 ? AMBER : SUB, fontWeight: 700 }}>{x.overdue ? `due ${(x.deadline_date || '').slice(5)}` : `${x.age_days}d`}</span>
          </p>
          <button title="Done — promise kept" data-testid={`owed-done-${x.promise_id}`} onClick={(e) => mark(e, x, 'done')} style={{ ...mini, padding: '1px 6px', fontSize: 10, color: TEAL }}>✓</button>
          {!x.actioned && <button title="Add to Desk" data-testid={`owed-add-${x.promise_id}`} onClick={(e) => add(e, x)} style={{ ...mini, padding: '1px 6px', fontSize: 10, color: AMBER }}>+</button>}
          <button title="Dismiss — no longer relevant" data-testid={`owed-dismiss-${x.promise_id}`} onClick={(e) => mark(e, x, 'dismiss')} style={{ ...mini, padding: '1px 6px', fontSize: 10, color: SUB }}>✕</button>
        </div>
      ))}
    </div>
  );
};

// ── FULL VIEW ────────────────────────────────────────────────────────────────
const DebriefViewInner = ({ ctx, onClose, initial }) => {
  const [date, setDate] = useState(null);
  const [d, setD] = useState(null);
  const [dates, setDates] = useState([]);
  const [cal, setCal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(initial?.person ? 'people' : 'day');
  const [openPid, setOpenPid] = useState(initial?.person || null);

  const load = useCallback((dt) => {
    setErr(null);
    ctx.req('GET', `/api/admin/desk/debrief${dt ? `?date=${dt}` : ''}`)
      .then((x) => { setD(x); setDate(x.date); })
      .catch(() => setErr('Fieldy unreachable — will retry'));
  }, [ctx.req]);
  useEffect(() => { load(null); ctx.req('GET', '/api/admin/desk/debrief/dates').then((x) => setDates(x.dates || [])).catch(() => {}); }, [load, ctx.req]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (ctx.demo) return null; // inaccessible while presenting

  const refresh = async () => {
    setBusy(true); setErr(null);
    try { await ctx.req('POST', '/api/admin/desk/debrief/refresh', { date }); load(date); ctx.showToast('Debrief refreshed'); }
    catch (e) { setErr(String(e?.message || '').includes('not connected') ? 'Fieldy not connected — needs webhook token' : 'Fieldy unreachable — will retry'); }
    setBusy(false);
  };
  const destroyDay = async () => {
    if (!date) return;
    if (!window.confirm(`Delete the debrief for ${fmtDate(date)} forever?\nThis cannot be undone. People + linked promises stay.`)) return;
    setBusy(true);
    try {
      await ctx.req('DELETE', `/api/admin/desk/debrief?date=${date}&confirm=DELETE`);
      ctx.showToast('🗑 Debrief day deleted');
      load(shiftDate(date, -1));
    } catch (e) { ctx.showToast('Delete failed: ' + (e?.message || 'unknown')); }
    setBusy(false);
  };
  const addPromise = async (pid) => {
    try { await ctx.req('POST', '/api/admin/desk/debrief/promise_add', { date, promise_id: pid }); ctx.showToast('✅ Added to Desk'); load(date); ctx.load(); }
    catch { ctx.showToast('Could not add — try again'); }
  };
  const addPerson = async (p, as) => {
    try {
      const r = await ctx.req('POST', '/api/admin/desk/debrief/person_add', { name: p.name, context: p.context, as });
      ctx.showToast(as === 'lead' ? `✅ ${p.name} added as lead` : `✅ Logged to ${r.logged_to}`);
    } catch (e) { ctx.showToast(String(e?.message || '').includes('404') ? 'No matching client — try add as lead' : 'Could not add'); }
  };

  const dg = d?.digest;
  const isToday = d && date === d.today;
  return createPortal(
    <div data-testid="debrief-view" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(8,9,11,0.82)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '5vh 14px' }}>
      <div onClick={(e) => e.stopPropagation()} className="desk-scroll"
        style={{ width: 'min(680px, 96vw)', background: '#0B0C0E', border: `1px solid ${BORDER}`, borderRadius: 18, padding: '20px 22px 26px', boxShadow: '0 40px 120px rgba(0,0,0,0.6)' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 18 }}>🎧</span>
          <h2 style={{ margin: 0, fontFamily: HEAD, fontSize: 17, fontWeight: 800, color: TXT, flex: 1 }}>{tab === 'people' ? 'The Debrief — People' : `The Debrief — ${date ? fmtDate(date) : '…'}`}</h2>
          {tab === 'day' && (
            <>
              <button data-testid="debrief-prev" onClick={() => load(shiftDate(date, -1))} style={mini}>◀</button>
              <button data-testid="debrief-next" onClick={() => isToday ? null : load(shiftDate(date, 1))} style={{ ...mini, opacity: isToday ? 0.35 : 1 }}>▶</button>
              <button data-testid="debrief-cal-btn" onClick={() => setCal(!cal)} style={mini}>📅</button>
              {isToday && <button data-testid="debrief-refresh" onClick={refresh} disabled={busy} style={{ ...mini, color: TEAL }}>{busy ? '…' : '↻ refresh today'}</button>}
              {dg && <button data-testid="debrief-destroy" onClick={destroyDay} disabled={busy}
                title="Delete this debrief day (with confirm)"
                style={{ ...mini, color: MAGENTA, borderColor: 'rgba(236,124,36,0.4)' }}>🗑</button>}
            </>
          )}
          <button data-testid="debrief-close" onClick={onClose} style={{ ...mini, color: MAGENTA }}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
          <button data-testid="debrief-tab-day" onClick={() => setTab('day')}
            style={{ ...mini, fontWeight: 800, color: tab === 'day' ? TEAL : SUB, borderColor: tab === 'day' ? TEAL : BORDER }}>Day</button>
          <button data-testid="debrief-tab-people" onClick={() => { setOpenPid(null); setTab('people'); }}
            style={{ ...mini, fontWeight: 800, color: tab === 'people' ? TEAL : SUB, borderColor: tab === 'people' ? TEAL : BORDER }}>People</button>
        </div>
        {tab === 'people' && <PeopleTab ctx={ctx} openPid={openPid} focusPid={initial?.promise} onJumpDay={(dt) => { setTab('day'); load(dt); }} />}
        {tab === 'day' && (<>
        {cal && (
          <div data-testid="debrief-calendar" className="desk-scroll" style={{ ...CARD, marginTop: 10, maxHeight: 220, overflowY: 'auto' }}>
            {(() => {
              const months = {};
              const all = new Set([...dates]);
              const today = d?.today || date;
              for (let i = 0; i < 60; i++) all.add(shiftDate(today, -i));
              [...all].sort().reverse().forEach((dt) => {
                const k = dt.slice(0, 7);
                (months[k] = months[k] || []).push(dt);
              });
              return Object.entries(months).map(([m, ds]) => (
                <div key={m} style={{ marginBottom: 8 }}>
                  <p style={{ margin: '0 0 4px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, color: SUB, letterSpacing: '0.1em' }}>
                    {new Date(m + '-15T12:00:00').toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }).toUpperCase()}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {ds.sort().map((dt) => {
                      const has = dates.includes(dt);
                      return (
                        <button key={dt} data-testid={`debrief-day-${dt}`} onClick={() => { load(dt); setCal(false); }}
                          style={{ ...mini, padding: '3px 7px', fontSize: 10, color: has ? TEAL : SUB, opacity: has ? 1 : 0.5, borderColor: dt === date ? TEAL : BORDER }}>
                          {dt.slice(8)}{has ? ' •' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
        {err && <p style={{ ...bodyP, marginTop: 12, color: AMBER }}>{err}</p>}
        {d && !d.connected && (
          <div data-testid="debrief-needskey" style={{ ...CARD, marginTop: 14, borderStyle: 'dashed', textAlign: 'center' }}>
            <p style={{ ...bodyP, fontWeight: 700 }}>Fieldy not connected — needs webhook token</p>
            <p style={{ ...bodyP, marginTop: 6, color: SUB, fontSize: 11.5 }}>Fieldy has no pull API. In the Fieldy app: Settings → Developer Settings → Webhook Endpoint URL — paste the TravelTrucks endpoint (Col supplies the token; it lives server-side only).</p>
          </div>
        )}
        {d && d.connected && !dg && (
          <p style={{ ...bodyP, marginTop: 16, color: SUB }}>No debrief for this day{isToday ? ' yet — it lands at 5pm, or hit ↻ refresh today' : ''}.</p>
        )}
        {dg && (
          <>
            <p style={secH}>Your day</p>
            <p data-testid="debrief-narrative" style={bodyP}>{dg.quiet ? 'Quiet day — nothing recorded.' : dg.narrative_summary}</p>

            {(dg.promises || []).length > 0 && (
            <>
                <p style={secH}>⚡ What you promised</p>
                {!isToday && <p style={{ ...bodyP, fontSize: 11, color: SUB, margin: '0 0 8px' }}>from the past — review if still relevant</p>}
                {(dg.promises || []).map((p) => <PromiseCard key={p.promise_id} p={p} past={!isToday} onAdd={() => addPromise(p.promise_id)} />)}
              </>
            )}
            {(dg.key_moments || []).length > 0 && (
              <>
                <p style={secH}>What matters</p>
                {(dg.key_moments || []).map((m, i) => <p key={i} style={{ ...bodyP, margin: '0 0 6px' }}>· {m}</p>)}
              </>
            )}
            {(dg.people || []).length > 0 && (
              <>
                <p style={secH}>Who you spoke to</p>
                {(dg.people || []).map((p, i) => (
                  <div key={i} style={{ ...CARD, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <p data-testid={`debrief-person-name-${i}`} onClick={() => { setOpenPid(pidOf(p.name)); setTab('people'); }}
                        style={{ ...bodyP, fontWeight: 700, cursor: 'pointer', color: TEAL, textDecorationLine: 'underline', textDecorationColor: `${TEAL}44`, textUnderlineOffset: 3 }}>{p.name}</p>
                      <p style={{ ...bodyP, fontSize: 11.5, color: SUB }}>{p.context}</p>
                    </div>
                    <button data-testid={`debrief-person-lead-${i}`} onClick={() => addPerson(p, 'lead')} style={{ ...mini, color: TEAL }}>+ add as lead</button>
                    <button data-testid={`debrief-person-note-${i}`} onClick={() => addPerson(p, 'client_note')} style={{ ...mini, color: VIOLET }}>✎ log to client</button>
                  </div>
                ))}
              </>
            )}
            {(dg.actions || []).length > 0 && (
              <>
                <p style={secH}>Actions</p>
                {(dg.actions || []).map((a, i) => {
                  const text = typeof a === 'string' ? a : (a?.what || '');
                  const who = typeof a === 'object' && a ? a.to_whom : null;
                  const due = typeof a === 'object' && a ? a.deadline_date : null;
                  return (
                    <p key={i} style={{ ...bodyP, margin: '0 0 6px' }}>
                      ☐ {text}
                      {who && <span style={{ color: SUB, fontSize: 11.5 }}> · to {who}</span>}
                      {due && <span style={{ color: AMBER, fontSize: 11 }}> · {due}</span>}
                    </p>
                  );
                })}
              </>
            )}
          </>
        )}
        </>)}
      </div>
    </div>,
    document.body
  );
};

const PromiseCard = ({ p, past = false, onAdd }) => {
  const [quote, setQuote] = useState(false);
  return (
    <div data-testid={`debrief-promise-${p.promise_id}`} onClick={() => setQuote(!quote)}
      style={{ ...CARD, marginBottom: 8, cursor: 'pointer', borderColor: p.actioned || past ? BORDER : `${AMBER}66` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <p style={{ ...bodyP, fontWeight: 700 }}>{p.what}</p>
          <p style={{ ...bodyP, fontSize: 11.5, color: SUB }}>to {p.to_whom || '—'} · heard: {p.deadline_heard || '—'}{p.deadline_date ? ` (${p.deadline_date})` : ''}</p>
        </div>
        {p.actioned
          ? <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: TEAL, fontWeight: 800 }}>✓ on the Desk</span>
          : <button data-testid={`debrief-promise-add-${p.promise_id}`} onClick={(e) => { e.stopPropagation(); onAdd(); }}
              style={{ ...mini, color: '#0E0F11', background: AMBER, border: 'none', fontWeight: 800 }}>+ Add to Desk</button>}
      </div>
      {quote && p.source_quote && <p style={{ ...bodyP, marginTop: 8, fontSize: 11.5, fontStyle: 'italic', color: SUB }}>“{p.source_quote}”</p>}
    </div>
  );
};

export const DebriefView = (props) => (
  <DebriefErrorBoundary onClose={props.onClose}>
    <DebriefViewInner {...props} />
  </DebriefErrorBoundary>
);



// ─────────────────────────────────────────────────────────────────────────────
// TODAY'S ACTIONS — a SEPARATE widget from the Debrief. The Debrief tells the
// STORY of the day; this widget turns extracted promises/actions into real
// Desk tasks with one tap. Suggest-then-confirm — never silent auto-add.
// Rows carry over from the last 7 days with an age chip so nothing gets lost.
// ─────────────────────────────────────────────────────────────────────────────
const ROUTE_META = {
  quote:    { icon: '📋', label: 'Quote' },
  callback: { icon: '☎', label: 'Call back' },
  material: { icon: '🔧', label: 'Order/pickup' },
  meeting:  { icon: '📅', label: 'Meeting' },
  task:     { icon: '✅', label: 'Task' },
  null:     { icon: '✅', label: 'Task' },
};
const rowKey = (r) => `${r.date}::${r.kind}::${r.row_id}`;
const confDot = (c) => c >= 0.8 ? TEAL : c >= 0.6 ? AMBER : MAGENTA;

export const TodayActionsTile = ({ ctx }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [showQuoteFor, setShowQuoteFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectByRow, setProjectByRow] = useState({}); // rowKey -> project_id
  const [projectPickerFor, setProjectPickerFor] = useState(null); // rowKey when open

  const load = useCallback(() => {
    ctx.req('GET', '/api/admin/desk/actions/today').then(setData).catch(() => setData({ actions: [] }));
  }, [ctx.req]);
  useEffect(() => { load(); const t = setInterval(load, 90000); return () => clearInterval(t); }, [load]);

  // Projects list — for the "route to a project" picker chip. Cached
  // per tile mount; small list, cheap fetch. Auto-hidden when there
  // are no active projects (no chip clutter).
  useEffect(() => {
    if (ctx.demo) return;
    ctx.req('GET', '/api/admin/projects?archived=false')
      .then((r) => setProjects(r.projects || []))
      .catch(() => setProjects([]));
  }, [ctx.req, ctx.demo]);

  if (ctx.demo) {
    return (
      <div data-testid="actions-tile-masked" style={{ textAlign: 'center', padding: '14px 6px' }}>
        <p style={{ margin: 0, fontSize: 20 }}>✅</p>
        <p style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, fontWeight: 700 }}>Actions · hidden while presenting</p>
      </div>
    );
  }
  if (!data) return <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB }}>…</p>;
  const rows = data.actions || [];
  if (!rows.length) {
    return (
      <div data-testid="actions-tile-empty" style={{ textAlign: 'center', padding: '18px 8px' }}>
        <p style={{ margin: 0, fontSize: 22, opacity: 0.5 }}>✅</p>
        <p style={{ margin: '8px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, lineHeight: 1.55 }}>
          Nothing needing action from today&apos;s conversations.<br/>
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>Fieldy 5pm digest will refresh this.</span>
        </p>
      </div>
    );
  }

  const toggle = (r) => {
    const k = rowKey(r);
    setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  };
  const addOne = async (r) => {
    setBusy(true);
    const k = rowKey(r);
    const projectId = projectByRow[k] || null;
    try {
      const res = await ctx.req('POST', '/api/admin/desk/actions/add', { items: [{ date: r.date, row_id: r.row_id, kind: r.kind, project_id: projectId }] });
      const c = (res.created || [])[0];
      const routeLabel = ROUTE_META[c?.route]?.label || 'Task';
      const who = c?.person_match?.business_name || c?.person_match?.first_name;
      const projSuffix = c?.project_name ? ` · ◈ ${c.project_name}` : '';
      ctx.showToast(`✅ Added to Desk · ${routeLabel}${who ? ` → ${who}` : ''}${projSuffix}`);
      load(); ctx.load?.();
    } catch (e) { ctx.showToast('Could not add: ' + (e?.message || 'unknown')); }
    setBusy(false);
  };
  const addSelected = async () => {
    if (!selected.size) return;
    setBusy(true);
    const items = rows.filter((r) => selected.has(rowKey(r))).map((r) => ({ date: r.date, row_id: r.row_id, kind: r.kind, project_id: projectByRow[rowKey(r)] || null }));
    try {
      const res = await ctx.req('POST', '/api/admin/desk/actions/add', { items });
      ctx.showToast(`✅ Added ${res.count} to Desk`);
      setSelected(new Set()); load(); ctx.load?.();
    } catch (e) { ctx.showToast('Could not add: ' + (e?.message || 'unknown')); }
    setBusy(false);
  };
  const pickProject = (rowK, projectId) => {
    setProjectByRow((s) => ({ ...s, [rowK]: projectId || null }));
    setProjectPickerFor(null);
  };
  const dismiss = async (r) => {
    if (!window.confirm(`Dismiss "${r.what}"? It won't appear here again but stays in the debrief history.`)) return;
    try {
      await ctx.req('POST', '/api/admin/desk/actions/dismiss', { date: r.date, row_id: r.row_id, kind: r.kind });
      ctx.showToast('⌫ Dismissed');
      load();
    } catch (e) { ctx.showToast('Could not dismiss'); }
  };

  const hasHi = rows.some((r) => r.confidence >= 0.8 && !r.age_days);

  return (
    <div data-testid="actions-tile" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexShrink: 0 }}>
        <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 700, color: TXT }}>
          <b style={{ color: AMBER }}>{rows.length}</b> action{rows.length === 1 ? '' : 's'} pending
        </p>
        <div style={{ flex: 1 }} />
        {selected.size > 0 && (
          <button data-testid="actions-add-selected" disabled={busy} onClick={addSelected}
            style={{ ...mini, background: AMBER, color: '#0E0F11', border: 'none', fontWeight: 800, padding: '3px 10px', fontSize: 10.5, cursor: 'pointer' }}>
            + Add {selected.size}
          </button>
        )}
        {selected.size === 0 && hasHi && (
          <button data-testid="actions-add-all-hi" disabled={busy}
            onClick={() => { const hi = new Set(rows.filter((r) => r.confidence >= 0.8 && !r.age_days).map(rowKey)); setSelected(hi); }}
            style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.4)', padding: '3px 10px', fontSize: 10.5 }}>
            select high-confidence
          </button>
        )}
      </div>
      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {rows.map((r) => {
          const k = rowKey(r);
          const sel = selected.has(k);
          const meta = ROUTE_META[r.route_hint || 'null'] || ROUTE_META.null;
          return (
            <div key={k} data-testid={`action-row-${r.row_id}`} style={{ display: 'flex', gap: 8, padding: '7px 8px', margin: '0 0 5px', borderRadius: 10, background: sel ? 'rgba(247,150,64,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${sel ? 'rgba(247,150,64,0.4)' : BORDER}` }}>
              <input type="checkbox" data-testid={`action-select-${r.row_id}`} checked={sel} onChange={() => toggle(r)}
                style={{ marginTop: 2, accentColor: AMBER, cursor: 'pointer', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 11, flexShrink: 0 }}>{meta.icon}</span>
                  <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, fontWeight: 700, color: TXT, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.what}</p>
                  <span title={`confidence: ${(r.confidence * 100).toFixed(0)}%`} style={{ width: 6, height: 6, borderRadius: '50%', background: confDot(r.confidence), flexShrink: 0 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                  {r.to_whom && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: r.person_match ? TEAL : SUB, fontWeight: 700 }}>{r.person_match ? '→ ' : 'to '}{r.person_match?.business_name || r.person_match?.first_name || r.to_whom}{r.person_match ? ' ✓' : ''}</span>}
                  {r.deadline_date && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: AMBER, fontWeight: 700 }}>· {r.deadline_date}</span>}
                  {!r.deadline_date && r.deadline_heard && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB }}>· {r.deadline_heard}</span>}
                  {r.age_days > 0 && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: r.age_days > 3 ? MAGENTA : SUB, fontWeight: 700, background: r.age_days > 3 ? 'rgba(236,124,36,0.09)' : 'transparent', padding: '0 5px', borderRadius: 4 }}>{r.age_days}d old</span>}
                </div>
                {showQuoteFor === k && r.source_quote && (
                  <p style={{ margin: '5px 0 2px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontStyle: 'italic', color: SUB, lineHeight: 1.5, background: 'rgba(255,255,255,0.03)', padding: '5px 8px', borderRadius: 6 }}>you said: “{r.source_quote}”</p>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {r.source_quote && (
                    <button data-testid={`action-quote-${r.row_id}`} onClick={() => setShowQuoteFor(showQuoteFor === k ? null : k)}
                      style={{ ...mini, padding: '1px 8px', fontSize: 9.5, color: SUB, borderColor: BORDER }}>{showQuoteFor === k ? 'hide' : 'source'}</button>
                  )}
                  {projects.length > 0 && (() => {
                    const selectedPid = projectByRow[k];
                    const proj = selectedPid && projects.find((p) => p.project_id === selectedPid);
                    return (
                      <div style={{ position: 'relative' }}>
                        <button data-testid={`action-project-picker-${r.row_id}`}
                          onClick={() => setProjectPickerFor(projectPickerFor === k ? null : k)}
                          title={proj ? `Routing to project: ${proj.name}` : 'Optional: route this action to a project'}
                          style={{ ...mini, padding: '1px 8px', fontSize: 9.5, color: proj ? '#0E0F11' : TEAL, background: proj ? TEAL : 'transparent', border: `1px solid ${proj ? TEAL : 'rgba(84,209,151,0.4)'}`, fontWeight: 800, cursor: 'pointer' }}>
                          ◈ {proj ? proj.name.slice(0, 16) : 'project'}
                        </button>
                        {projectPickerFor === k && (
                          <div data-testid={`project-picker-menu-${r.row_id}`}
                            style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#17181B', border: '1px solid rgba(84,209,151,0.35)', borderRadius: 8, padding: 4, minWidth: 180, maxHeight: 220, overflowY: 'auto', zIndex: 5000, boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
                            <button data-testid={`project-pick-none-${r.row_id}`}
                              onClick={(e) => { e.stopPropagation(); pickProject(k, null); }}
                              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 8px', border: 'none', background: !selectedPid ? 'rgba(255,255,255,0.05)' : 'transparent', color: SUB, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', borderRadius: 5 }}>
                              — none (task only)
                            </button>
                            {projects.map((p) => {
                              const isSel = selectedPid === p.project_id;
                              return (
                                <button key={p.project_id} data-testid={`project-pick-${r.row_id}-${p.project_id}`}
                                  onClick={(e) => { e.stopPropagation(); pickProject(k, p.project_id); }}
                                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 8px', border: 'none', background: isSel ? 'rgba(84,209,151,0.12)' : 'transparent', color: TXT, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontWeight: isSel ? 800 : 500, cursor: 'pointer', borderRadius: 5 }}>
                                  <span style={{ color: TEAL }}>◈</span> {p.name}
                                  {p.client_tag && <span style={{ color: SUB, marginLeft: 4, fontWeight: 500 }}>· {p.client_tag}</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <button data-testid={`action-add-${r.row_id}`} disabled={busy} onClick={() => addOne(r)}
                    style={{ ...mini, padding: '1px 10px', fontSize: 10, color: '#0E0F11', background: AMBER, border: 'none', fontWeight: 800, cursor: 'pointer' }}>+ Add to task list</button>
                  <button data-testid={`action-dismiss-${r.row_id}`} onClick={() => dismiss(r)}
                    style={{ ...mini, padding: '1px 8px', fontSize: 9.5, color: SUB, borderColor: BORDER }}>⌫</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
